// @ts-nocheck
import React, { FC, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, LineChart, Line,
} from "recharts";
import {
  DefinitionTooltip,
} from "src/components/common";
import {
  getOrganicContentSectionDefinitionInfo,
} from "../OrganicMediaDefinitions";
import { formatNumber } from "src/utils/numberFormat";
import {
  OverTimeTooltip,
  formatTrendDateByGranularity,
  formatTrendMetricLabel,
  isPercentTrendCard,
  ORGANIC_CHART_COLORS,
  type TrendGranularity,
} from "./FacebookContentHelpers";
import "../../../styles/FacebookContentReport/FacebookContentTrends.scss";

type TrendComparisonCard = {
  title: string;
  metricKey: string;
};

type TrendLegendItem = {
  label: string;
  color: string;
};

const FacebookContentTrends: FC<{
  trendGranularity: TrendGranularity;
  setTrendGranularity: (_v: TrendGranularity) => void;
  trendComparisonData: Array<Record<string, string | number>>;
  trendComparisonCards: TrendComparisonCard[];
  trendLegendItems: TrendLegendItem[];
}> = ({
  trendGranularity, setTrendGranularity, trendComparisonData,
  trendComparisonCards, trendLegendItems,
}) => {
  const { t, i18n } = useTranslation();
  return (
    <div className="organic-content-panel">
      <div className="organic-content-panel__header">
        <div className="organic-content-panel__title">
          <h3>{t("organicMedia.facebook.sections.trends30d")}</h3>
          <DefinitionTooltip
            label={t("organicMedia.facebook.sections.trends30d")}
            info={getOrganicContentSectionDefinitionInfo("trendsLast30Days", t)}
            formulaTitle={t("organicMedia.common.formula")}
          />
        </div>
        <div
          className="organic-content-panel__segmented"
          role="group"
          aria-label={t("organicMedia.contentReport.trendGranularity")}
        >
          {(["daily", "monthly"] as TrendGranularity[]).map(option => (
            <button
              key={option}
              type="button"
              className={
                trendGranularity === option
                  ? "organic-content-panel__segmented-button organic-content-panel__segmented-button--active"
                  : "organic-content-panel__segmented-button"
              }
              onClick={() => setTrendGranularity(option)}
            >
              {t(`organicMedia.contentReport.${option}`)}
            </button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={380}>
        <LineChart data={trendComparisonData} margin={{ top: 4, right: 4, left: -4, bottom: 0 }}>
          <CartesianGrid stroke="#e4e8ef" strokeDasharray="4 4" />
          <XAxis
            dataKey="date"
            tick={{ fill: "#5e6a7d", fontSize: 12 }}
            tickFormatter={value => formatTrendDateByGranularity(value, trendGranularity, i18n.language)}
            axisLine={{ stroke: "#d7dde7" }}
            tickLine={false}
            interval="preserveStartEnd"
            height={30}
          />
          <YAxis
            yAxisId="pct"
            hide={!trendComparisonCards.some(card => isPercentTrendCard(card))}
            tick={{ fill: "#5e6a7d", fontSize: 12 }}
            axisLine={{ stroke: "#d7dde7" }}
            tickLine={false}
            width={38}
          />
          <YAxis
            yAxisId="count"
            orientation="right"
            hide={!trendComparisonCards.some(card => !isPercentTrendCard(card))}
            tick={{ fill: "#5e6a7d", fontSize: 12 }}
            axisLine={{ stroke: "#d7dde7" }}
            tickLine={false}
            width={48}
            tickFormatter={value => formatNumber(Number(value), {}, i18n.language)}
          />
          <Tooltip
            content={<OverTimeTooltip />}
            labelFormatter={(value: string | number) => formatTrendDateByGranularity(value, trendGranularity, i18n.language)}
            allowEscapeViewBox={{ x: false, y: false }}
            wrapperStyle={{ pointerEvents: "none" }}
            position={{ y: 18 }}
          />
          {trendComparisonCards.map((card, index) => (
            <Line
              key={card.title}
              yAxisId={isPercentTrendCard(card) ? "pct" : "count"}
              type="monotone"
              dataKey={card.title}
              name={formatTrendMetricLabel(card.title, t)}
              stroke={ORGANIC_CHART_COLORS[index % ORGANIC_CHART_COLORS.length]}
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 4 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <div className="organic-content-panel__trend-legend" aria-hidden>
        {trendLegendItems.map(item => (
          <div key={item.label} className="organic-content-panel__trend-legend-item">
            <span
              className="organic-content-panel__trend-legend-swatch"
              style={{ "--legend-color": item.color } as CSSProperties}
            />
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FacebookContentTrends;
